import * as React from 'react';
import { useTranslation } from 'react-i18next';
import apiService, { Project, SubProject } from '../../services/api';
import { RefreshCw, ChevronDown } from 'lucide-react';
import type { SolutionId } from './SolutionsGrid';
import { directionForLanguage } from '../../i18n/config';
import './ProjectSelector.css';

/** Domain/sub_domain filter per solution (matches backend project types). */
const SOLUTION_CONFIG: Record<SolutionId, { domain: string; sub_domain: string }> = {
  master_cross_checks: {
    domain: 'Real Estate',
    sub_domain: 'Master Cross Checks',
  },
  construction_loan: {
    domain: 'Real Estate',
    sub_domain: 'Construction Loan Agreement',
  },
};

interface ProjectSelectorProps {
  solutionId: SolutionId;
  onSubProjectSelect: (subProjectId: string) => void;
  onProjectSelect?: (projectId: string) => void;
  onCreateNewSubProject?: () => void;
  selectedSubProjectId?: string;
  selectedProjectId?: string;
  refreshTrigger?: number;
  isAuthenticated?: boolean;
  /** When true (e.g. MCC / CLA guided client create), collapse the panel to free vertical space. */
  guidedSetupActive?: boolean;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  solutionId,
  onSubProjectSelect,
  onProjectSelect,
  onCreateNewSubProject,
  selectedSubProjectId,
  selectedProjectId,
  refreshTrigger,
  isAuthenticated = true,
  guidedSetupActive = false,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [subProjects, setSubProjects] = React.useState<SubProject[]>([]);
  const [internalSelectedProjectId, setInternalSelectedProjectId] = React.useState<string>('');
  // Separate flags so an in-flight sub-project fetch never disables the project
  // dropdown (and vice-versa). A single shared flag could get stuck `true` during
  // auth/session races, leaving both selectors permanently greyed out.
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [loadingSubProjects, setLoadingSubProjects] = React.useState(false);

  const projectsFetchGenRef = React.useRef(0);
  const subProjectsFetchGenRef = React.useRef(0);
  const activeProjectIdForSubRef = React.useRef<string>('');

  // Use prop if provided, otherwise use internal state
  const activeProjectId = selectedProjectId || internalSelectedProjectId;
  activeProjectIdForSubRef.current = activeProjectId;
  const [error, setError] = React.useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (guidedSetupActive) {
      setIsCollapsed(true);
    }
  }, [guidedSetupActive]);

  // Load projects when solution, auth, or mount changes
  const loadProjects = React.useCallback(async () => {
    if (!isAuthenticated) {
      setProjects([]);
      setSubProjects([]);
      setError(null);
      return;
    }

    const fetchGen = ++projectsFetchGenRef.current;
    const config = SOLUTION_CONFIG[solutionId];
    try {
      setLoadingProjects(true);
      setError(null);
      const response = await apiService.getProjects();
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      if (response.success && response.data) {
        const filteredProjects = response.data.filter(
          (project) =>
            project.domain === config.domain && project.sub_domain === config.sub_domain
        );
        setProjects(filteredProjects);
      } else {
        setError(t('projectSelector.errors.loadProjects'));
      }
    } catch (err: any) {
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      console.error('Error loading projects:', err);

      // Provide user-friendly error messages
      if (err.response?.status === 401) {
        setError(t('projectSelector.errors.loadProjects'));
      } else if (err.response?.status === 404) {
        setError(t('projectSelector.errors.endpointNotFound'));
      } else if (err.message?.includes('No projects endpoint available')) {
        setError(t('projectSelector.errors.cannotConnect'));
      } else {
        setError(err.message || t('projectSelector.errors.loadProjects'));
      }
    } finally {
      if (fetchGen === projectsFetchGenRef.current) {
        setLoadingProjects(false);
      }
    }
  }, [solutionId, isAuthenticated, t]);

  React.useEffect(() => {
    setProjects([]);
    setSubProjects([]);
    if (!isAuthenticated) {
      // Invalidate any in-flight fetches and clear loading so the selectors are
      // never left disabled when we come back authenticated.
      projectsFetchGenRef.current += 1;
      subProjectsFetchGenRef.current += 1;
      setLoadingProjects(false);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }
    loadProjects();
  }, [loadProjects, isAuthenticated]);

  // Load sub-projects when project is selected, projects are known, and auth is ready
  React.useEffect(() => {
    if (!isAuthenticated) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    if (!activeProjectId) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    if (projects.length > 0 && !projects.some((project) => project.id === activeProjectId)) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    // Wait until the project list has loaded before fetching sub-projects, but
    // never gate on the sub-project loading flag itself (that caused a refetch
    // loop that kept the selectors disabled).
    if (loadingProjects && projects.length === 0) {
      return;
    }

    loadSubProjects(activeProjectId);
  }, [activeProjectId, refreshTrigger, isAuthenticated, projects, loadingProjects]);

  const loadSubProjects = async (projectId: string) => {
    const requestedProjectId = projectId;
    const fetchGen = ++subProjectsFetchGenRef.current;
    try {
      if (!apiService.isAuthenticated()) {
        return;
      }

      setLoadingSubProjects(true);
      setError(null);
      const domainHint =
        solutionId === 'construction_loan' ? 'construction-loan' : 'real-estate';
      const response = await apiService.getSubProjects(projectId, domainHint);
      if (
        fetchGen !== subProjectsFetchGenRef.current ||
        requestedProjectId !== activeProjectIdForSubRef.current
      ) {
        return;
      }
      if (response.success && response.data) {
        setSubProjects(response.data);
      } else {
        setError(t('projectSelector.errors.loadSubProjects'));
      }
    } catch (err: any) {
      if (
        fetchGen !== subProjectsFetchGenRef.current ||
        requestedProjectId !== activeProjectIdForSubRef.current
      ) {
        return;
      }
      console.error('Error loading sub-projects:', err);
      if (err.response?.status === 403) {
        setError(t('projectSelector.errors.noAccess'));
      } else if (err.response?.status === 404) {
        setError(t('projectSelector.errors.notFound'));
      } else {
        setError(err.message || t('projectSelector.errors.loadSubProjects'));
      }
    } finally {
      if (
        fetchGen === subProjectsFetchGenRef.current &&
        requestedProjectId === activeProjectIdForSubRef.current
      ) {
        setLoadingSubProjects(false);
      }
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    if (!selectedProjectId) {
      // Only update internal state if prop is not provided
      setInternalSelectedProjectId(projectId);
    }
    onSubProjectSelect(''); // Clear sub-project selection
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  const handleSubProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__create_new__' && onCreateNewSubProject) {
      onSubProjectSelect('');
      onCreateNewSubProject();
      return;
    }
    onSubProjectSelect(value);
  };

  return (
    <div
      className={`project-selector ${isCollapsed ? 'collapsed' : ''}`}
      dir={chromeDir}
    >
      <div className="project-selector-header">
        <div className="project-selector-header-left">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="collapse-toggle-button"
            title={isCollapsed ? t('projectSelector.expand') : t('projectSelector.collapse')}
          >
            <ChevronDown
              size={18}
              className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}
            />
          </button>
          <h3>{t('projectSelector.title')}</h3>
        </div>
        <button
          type="button"
          onClick={() => loadProjects()}
          className="refresh-button"
          title={
            solutionId === 'master_cross_checks' || solutionId === 'construction_loan'
              ? t('projectSelector.refreshProjects')
              : t('projectSelector.refreshPlaybooks')
          }
          disabled={loadingProjects || loadingSubProjects}
        >
          <RefreshCw size={18} />
        </button>
      </div>
      {/* loadingProjects/loadingSubProjects are intentionally split: each selector
          only disables for its own fetch so neither stays stuck disabled. */}

      {!isCollapsed && (
        <>
          {error && <div className="error-message">{error}</div>}

          <div className="project-selector-content">
            <div className="project-selector-left">
              <div className="select-group">
                <label htmlFor="project-select">{t('projectSelector.selectProject')}</label>
                <select
                  id="project-select"
                  dir="auto"
                  value={activeProjectId}
                  onChange={handleProjectChange}
                  disabled={loadingProjects}
                >
                  <option value="">{t('projectSelector.placeholderProject')}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {activeProjectId && (
                <div className="select-group">
                  <label htmlFor="subproject-select">{t('projectSelector.selectSubProject')}</label>
                  <select
                    id="subproject-select"
                    dir="auto"
                    value={selectedSubProjectId || ''}
                    onChange={handleSubProjectChange}
                    disabled={loadingSubProjects || !activeProjectId}
                  >
                    <option value="">{t('projectSelector.placeholderSubProject')}</option>
                    {onCreateNewSubProject && (
                      <option value="__create_new__">{t('projectSelector.createNew')}</option>
                    )}
                    {subProjects.map((subProject) => (
                      <option key={subProject.id} value={subProject.id}>
                        {subProject.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
};

